// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import {
  ACTIVITY_FILTER_DEFAULTS,
  type ActivityEntry,
  type ActivityFilterState,
  ActivityTimeline,
} from "@/components/shared/activity-timeline";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";

function generateMockData(): ActivityEntry[] {
  const now = Date.now();
  const day = 86_400_000;
  return [
    {
      id: 1,
      timestamp: new Date(now - 0.4 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Store Override",
      field: "acme_store_12",
      oldValue: "false",
      newValue: "true",
    },
    {
      id: 2,
      timestamp: new Date(now - 0.8 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Chain Override",
      field: "walmart",
      oldValue: "500",
      newValue: "750",
    },
    {
      id: 3,
      timestamp: new Date(now - 1.5 * day).toISOString(),
      user: "ops@focal.systems",
      action: "restored",
      target: "Store Override",
      field: "acme_store_08",
    },
    {
      id: 4,
      timestamp: new Date(now - 3 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Default Value",
      oldValue: "300",
      newValue: "500",
      message: "Increased default to match new chain onboarding requirements.",
    },
    {
      id: 5,
      timestamp: new Date(now - 5 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "created",
      target: "Chain Override",
      field: "costco",
    },
    {
      id: 6,
      timestamp: new Date(now - 8 * day).toISOString(),
      user: "ops@focal.systems",
      action: "deleted",
      target: "Store Override",
      field: "acme_store_03",
    },
    {
      id: 7,
      timestamp: new Date(now - 12 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Setting Info",
      field: "description",
      oldValue: "Min workable items per scan",
      newValue: "Minimum workable items threshold per scan cycle",
    },
    {
      id: 8,
      timestamp: new Date(now - 18 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Store Override",
      field: "kroger_store_22",
      oldValue: "true",
      newValue: "false",
    },
    {
      id: 9,
      timestamp: new Date(now - 25 * day).toISOString(),
      user: "ops@focal.systems",
      action: "created",
      target: "Store Override",
      field: "walmart_store_55",
    },
    {
      id: 10,
      timestamp: new Date(now - 35 * day).toISOString(),
      user: "admin@focal.systems",
      action: "restored",
      target: "Chain Override",
      field: "acme",
    },
    {
      id: 11,
      timestamp: new Date(now - 45 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Setting Info",
      field: "fieldType",
      oldValue: "number",
      newValue: "boolean",
      message: "Migrated field type as part of settings schema v2 rollout.",
    },
    {
      id: 12,
      timestamp: new Date(now - 60 * day).toISOString(),
      user: "admin@focal.systems",
      action: "created",
      target: "Chain Override",
      field: "acme",
    },
    {
      id: 13,
      timestamp: new Date(now - 80 * day).toISOString(),
      user: "ops@focal.systems",
      action: "created",
      target: "Setting",
    },
  ];
}

const MOCK_DATA = generateMockData();

interface SettingActivityTabProps {
  settingName: string;
  initialFilters?: string;
}

export function SettingActivityTab({ settingName, initialFilters }: SettingActivityTabProps) {
  const navigate = useNavigate();
  const initial = useMemo(() => decodeFilters(initialFilters, ACTIVITY_FILTER_DEFAULTS), [initialFilters]);

  const handleFilterChange = useCallback(
    (values: ActivityFilterState) => {
      const encoded = encodeFilters(values, ACTIVITY_FILTER_DEFAULTS);
      navigate({
        to: "/settings/$settingName",
        params: { settingName },
        search: { t: "activity", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [navigate, settingName],
  );

  return <ActivityTimeline entries={MOCK_DATA} initialFilters={initial} onFilterChange={handleFilterChange} />;
}
